'use client';

import type { Client } from '@optimizely/optimizely-sdk';
import { usePathname } from 'next/navigation';
import type { JSX } from 'react';
import { ActionableIcon } from '@/app_components/common/ActionableIcon';
import { Icon } from '@/app_components/common/Icon';
import type { IconName } from '@/app_components/icons/constants';
import { BasicPopover } from '@/app_components/ui/BasicPopover';
import { trackOptimizelyEvent } from '@/app_lib/optimizely/utils';
import type { TrackEventParams } from '@/app_lib/services/Gtm/utils';
import { ContentPlacement } from '@/app_lib/types/analytics';
import { canUseNativeShare, shareCurrentPage } from '@/app_lib/utils/nativeShare';
import { cn } from '@/app_lib/utils/utils';
import { useOptimizely } from '@/main/lib/hooks/useOptimizely';

interface SocialLinkProps {
  href: string;
  name: string;
  icon: IconName;
  className: string;
  dataCy?: ContentPlacement;
  trackingData?: TrackEventParams;
  optimizely: Client;
}

const SocialLink = ({ href, name, className, dataCy, icon, trackingData, optimizely }: SocialLinkProps) => {
  const handleClick = (url: string) => {
    trackOptimizelyEvent(optimizely, 'shares', { shareUrl: url });
  };
  return (
    <ActionableIcon
      aria-label={icon === 'Mail' ? 'Share via email' : `Share on ${name}`}
      as='a'
      data-cy={dataCy}
      href={href || ''}
      onClick={() => handleClick(href)}
      rel='noopener noreferrer'
      target='_blank'
      trackingData={{
        data: {
          ...trackingData?.data,
          eventAction: `${trackingData?.data.eventAction} ${icon}`,
          eventLabel: href,
        },
        eventType: trackingData?.eventType,
      }}
    >
      <Icon
        className={className}
        icon={icon}
        mode='fill'
      />
    </ActionableIcon>
  );
};

interface SocialLinksDropdownProps {
  title: string;
  text?: string;
  className?: string;
  iconClassName?: string;
  contentClassName?: string;
  isShareButtonHovered?: boolean;
  enableMobileNativeShare?: boolean;
  trigger?: JSX.Element;
  trackingData?: TrackEventParams;
}

const getSocialLinks = (pathname: string | null, title: string) => {
  // Use fallback so share links render in Storybook (no router) and other non-route contexts
  const path = pathname ?? '/';
  const pageUrl = `https://fortune.com${path}`;
  const encodedTitle = encodeURIComponent(title);
  const setUtmParams = (name: string): string => {
    return `utm_campaign=social_share&utm_medium=social&utm_source=${name.toLowerCase()}`;
  };

  // Create URLs with UTM parameters for each platform
  const facebookUrl = `${pageUrl}?${setUtmParams('facebook')}`;
  const twitterUrl = `${pageUrl}?${setUtmParams('twitter')}`;
  const linkedinUrl = `${pageUrl}?${setUtmParams('linkedin')}`;
  const emailUrl = `${pageUrl}?${setUtmParams('email')}`;

  const socialLinks: {
    href: string;
    name: string;
    icon: IconName;
    iconClassName: string;
    dataCy: ContentPlacement | undefined;
  }[] = [
    {
      dataCy: ContentPlacement.FacebookIcon,
      href: `https://www.facebook.com/sharer.php/?u=${facebookUrl}`,
      icon: 'Facebook',
      iconClassName: '',
      name: 'Facebook',
    },
    {
      dataCy: ContentPlacement.TwitterIcon,
      href: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${twitterUrl}`,
      icon: 'Twitter',
      iconClassName: '',
      name: 'Twitter',
    },
    {
      dataCy: ContentPlacement.LinkedinIcon,
      href: `https://www.linkedin.com/shareArticle/?url=${linkedinUrl}`,
      icon: 'LinkedIn',
      iconClassName: '',
      name: 'LinkedIn',
    },
    {
      dataCy: ContentPlacement.EmailIcon,
      href: `mailto:?subject=${encodedTitle}&body=${emailUrl}`,
      icon: 'Mail',
      iconClassName: 'stroke-white dark:stroke-black',
      name: 'mailto',
    },
  ];
  return socialLinks;
};

export const SocialLinksDropdown = ({
  title,
  text,
  className,
  iconClassName,
  contentClassName,
  isShareButtonHovered,
  enableMobileNativeShare = false,
  trigger,
  trackingData,
}: SocialLinksDropdownProps) => {
  const pathname = usePathname();
  const socialLinksList = getSocialLinks(pathname, title);
  const useNativeShare = canUseNativeShare();
  const { userFlags: userVariants, optimizely } = useOptimizely();
  const showNativeShare = (userVariants?.nativeshare as { variant: string })?.variant === 'on';

  const defaultTrigger = (
    <ActionableIcon
      aria-label='Share article'
      className={cn('md:m-2', className)}
      data-cy={ContentPlacement.ShareIcon}
    >
      <Icon
        className={iconClassName}
        icon='Share2'
      />
    </ActionableIcon>
  );

  const nativeShareTrigger = (
    <ActionableIcon
      aria-label='Share article'
      className={cn('md:m-2', className)}
      data-cy={ContentPlacement.NativeShareIcon}
      onClick={() => {
        shareCurrentPage(optimizely, title, text);
      }}
    >
      <Icon
        className={iconClassName}
        icon='Share2'
      />
    </ActionableIcon>
  );

  const content = (
    <div className='flex md:mt-2 h-auto min-w-[40px] animate-slide-fade-in flex-col items-center md:space-y-4 rounded-sm border-1 stroke-secondary border-solid bg-pure py-2'>
      {socialLinksList.map((socialLink) => (
        <SocialLink
          className={socialLink.iconClassName}
          dataCy={socialLink.dataCy}
          href={socialLink.href || ''}
          icon={socialLink.icon}
          key={socialLink.name}
          name={socialLink.name}
          optimizely={optimizely}
          trackingData={trackingData}
        />
      ))}
    </div>
  );

  return useNativeShare && enableMobileNativeShare && showNativeShare ? (
    nativeShareTrigger
  ) : (
    <BasicPopover
      align='center'
      content={content}
      contentClassName={cn('z-body-popover mt-5', contentClassName)}
      isShareButtonHovered={isShareButtonHovered}
      shouldCloseOnScroll={false}
      trigger={trigger || defaultTrigger}
    />
  );
};
